import React, { createContext, useContext, useState } from 'react';

const SuccessContext = createContext(undefined);

/**
 * Custom hook to access the success context
 * 
 * @returns {Object} The success context
 * @throws Will throw an error if the hook is used outside of an LoadingProvider.
 * 
 * @category Context
 */
export const useSuccess = () => {
    const context = useContext(SuccessContext);
    if(!context){
        throw new Error('useSuccess must be used within a SuccessProvider');
    }
    return context;
}

/**
 * Provides app access to success context. Stores state for success, success messages, and functions that open modals on a successful action.
 * 
 * @param {React.ReactNode} props.children - children components to be wrapped by the provider
 * 
 * @returns {JSX.Element}
 * 
 * @category Context
 */
export const SuccessProvider = ({children}) => {
    const [success, setSuccess] = useState(false);
    const [successMessage, setSuccessMessage] = useState('');
    const [blurBG, setBlurBG] = useState(false);
    const [requestConfirmSuccess, setRequestConfirmSuccess] = useState(false);

    const showSuccess = (message = '', blur = false) => {
        setSuccess(true);
        setSuccessMessage(message);
    }

    const hideSuccess = () => {
        setSuccess(false);
        setSuccessMessage('');
        setBlurBG(false);
    }

    const timeoutSuccess = (message = '', timeout = 2000,  blur = false) => {
        setSuccess(true);
        setSuccessMessage(message);
        setBlurBG(blur);
        setTimeout(() => {
           hideSuccess();
        }, timeout);
    }

    const confirmSuccess = (message = '', blur = false) => {
        setSuccess(true);
        setSuccessMessage(message);
        setRequestConfirmSuccess(true);
        setBlurBG(blur);
    }

    const contextValue = {
        showSuccess,
        successMessage,
        success,
        hideSuccess,
        timeoutSuccess,
        confirmSuccess,
        blurBG,
        requestConfirmSuccess,
        setRequestConfirmSuccess
    }

    return (
        <SuccessContext.Provider value={contextValue}>
            {children}
        </SuccessContext.Provider>
    )
}